// subtasks/hooks/useSubTaskStatusButtonsController.ts
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { confirm } from "devextreme/ui/dialog";
import { StepDto } from "../../../../../dtos/StepDto";
import { PhaseWorkflowType } from "../../../../../dtos/PhaseWorkflowType";
import { StepWorkflowType } from "../../../../../dtos/StepWorkflowType";
import { useAppContext } from "../../../AppContext";
import { useTaskListContext, useTaskListSelectionState, TasklistMode } from "../../context/TaskListContext";
import { isClosedCycle } from "../../tasks/taskCycle";
import { GrantCommentAudienceType } from "../../../../../dtos/GrantCommentAudienceType";
import { StepStatus } from "../../../../../dtos/StepStatus";
import { addStepRevisionComment, setStepStatus } from "../../../../../api/v2/StepsApi";
import { getPhaseById } from "../../../../../api/v2/PhasesApi";
import { PermissionType } from "../../../../../dtos/PermissionType";
import { updateStepWithCompletionLogic, checkStepPermission, checkPhasePermission, checkStepTransition, isHybridParentType, isUploadOnlyPhase } from "../../../Utils";
import { GrantDto } from "../../../../../dtos/GrantDto";
import { deleteDefaultSubtask } from "../../../../../api/v2/TasklistsApi";

export type UseSubTaskStatusButtonsProps = {
    step: StepDto;
    readyDisabled?: React.RefObject<boolean>;
    completeDisabled?: React.RefObject<boolean>;
    requireComment?: boolean;
    onConfirm?: () => void;
    /** Optional: hide transitions in template mode */
    readOnly?: boolean;
    workflowType?: PhaseWorkflowType | null | undefined;
    grant?: GrantDto;
    onSave?: () => Promise<void>;
    onComplete?: () => Promise<void>;
    mode?: TasklistMode;
};

export function useSubTaskStatusButtonsController({
    step,
    readyDisabled,
    completeDisabled,
    requireComment = false,
    onConfirm,
    readOnly: readOnlyProp = false,
    workflowType,
    grant,
    onSave,
    onComplete,
    mode = "default",
}: UseSubTaskStatusButtonsProps) {
    const app = useAppContext();
    const taskListContext = useTaskListContext();
    const { selectedPhase: cyclePhase, selectedSubmissionId } = useTaskListSelectionState();

    // A closed cycle takes no status changes. Folded into readOnly rather than checked per button:
    // every handler here already no-ops on readOnly, and the None branch decides canMarkComplete
    // from a client-side transition check rather than the server's availableStepStatuses, so
    // suppressing actions server-side does not reach it (ticket 1023).
    const readOnly = readOnlyProp || isClosedCycle(cyclePhase, selectedSubmissionId);

    // Under a hybrid parent task (PhaseHybrid or StepHybrid) each subtask declares its own
    // workflow. Resolve the effective workflow type for this subtask so the button logic
    // below dispatches to the correct branch (None / Step / Phase).
    const effectiveWorkflowType: PhaseWorkflowType | null | undefined = (() => {
        if (!isHybridParentType(workflowType)) return workflowType;
        const subtaskType = step?.defaultStep?.settings?.workflowType;
        switch (subtaskType) {
            case StepWorkflowType.Phase: return PhaseWorkflowType.Phase;
            case StepWorkflowType.None: return PhaseWorkflowType.None;
            default: return PhaseWorkflowType.Step;
        }
    })();

    const newCommentTextbox = useRef<any>(null);
    const newCommentButton = useRef<any>(null);
    const newCommentTextValidationGroup = useRef<any>(null);
    const selectedAudience = useRef<number>(GrantCommentAudienceType.All);

    const [visible, setVisible] = useState(false);
    const [isReadyDisabled, setIsReadyDisabled] = useState(true);
    const [isCompleteDisabled, setIsCompleteDisabled] = useState(true);

    useEffect(() => {
        const tick = () => {
            const r = readyDisabled?.current ?? true;
            const c = completeDisabled?.current ?? true;
            setIsReadyDisabled(prev => (prev !== r ? r : prev));
            setIsCompleteDisabled(prev => (prev !== c ? c : prev));
        };
        const id = window.setInterval(tick, 100);
        return () => window.clearInterval(id);
    }, [readyDisabled, completeDisabled]);

    const available = useMemo(
        () => (Array.isArray(step?.availableStepStatuses) ? step.availableStepStatuses : []),
        [step?.availableStepStatuses]
    );

    const popupTitle = useMemo(
        () => `${step?.name ?? "Subtask"} Revision Request`,
        [step?.name]
    );

    const getActionMessage = useCallback((message: string | null, status: StepStatus) => {
        let full = `Are you sure you want to set the subtask status to ${message}?`;
        if (status === StepStatus.PENDING_REVISION) {
            full += ` No revision request is sent out until Revision is requested at the task level.`;
        }
        return full;
    }, []);

    // Deleting a template is a templates-admin action, so a grant never offers it. A core subtask
    // is seeded and the server refuses to delete it, so the action is not offered for one either -
    // otherwise the button always ends in an error toast.
    const canDeleteSubtask = useMemo(() => {
        if (mode !== "templates" || !step) return false;
        return step.defaultStep?.settings?.isCore !== true;
    }, [step, mode]);

    // Delete handler for template mode - defined early so it can be used in all return statements
    const handleDeleteSubtask = useCallback(async () => {
        if (!step?.defaultStep) return;

        const result = await confirm(
            `Are you sure you want to delete the subtask "${step.name}"? This action cannot be undone if the subtask has no instances.`,
            "Confirm Deletion"
        );

        if (!result) return;

        // Get moduleId from selected phase in context
        const selectedPhase = taskListContext.getSelectedPhase();
        const moduleId = selectedPhase?.defaultPhase?.moduleTypeId;

        if (!moduleId) {
            app.errorToast("Unable to determine module ID for deletion");
            return;
        }

        app.showLoadPanel();
        const deleteResult = await deleteDefaultSubtask(
            moduleId,
            step.defaultStep.phaseTypeId,
            step.defaultStep.id
        );

        if (deleteResult?.error) {
            app.errorToast(deleteResult.error.message);
        } else {
            // Clear the selected subtask and select the parent task
            taskListContext.setSelectedStep(null);
            taskListContext.setSelectedPhase(selectedPhase);
            // Refresh the task list
            await taskListContext.refreshSteps();
        }

        app.hideLoadPanel();
    }, [app, step, taskListContext]);

    const onStatusChange = useCallback(
        async (status: StepStatus) => {
            if (readOnly) return; // template mode: no-op
            const updated = await setStepStatus(step.id, status);
            if (!updated) return;
            if (updated.error) {
                app.errorToast(updated.error.message);
                return;
            }

            const updatedStep = updated.data as StepDto;
            const selectedPhase = taskListContext.getSelectedPhase();

            // Check if user will still have View permission for the step at its new status BEFORE updating selection
            const canStillViewStep = checkStepPermission(updatedStep, app, PermissionType.View, true, selectedPhase, grant?.id);

            if (canStillViewStep) {
                // User can still view - update selection and refresh
                taskListContext.setSelectedStep(updatedStep);
                await taskListContext.refreshSteps(updatedStep);

                // Refresh the parent phase to update its status icon
                if (selectedPhase?.id) {
                    const refreshedPhase = await getPhaseById(selectedPhase.id);
                    if (refreshedPhase?.data) {
                        taskListContext.setSelectedPhase(refreshedPhase.data);
                    }
                }
            } else {
                // User will lose view permission - clear selection first, then refresh
                taskListContext.clearSelections();
                taskListContext.setSelectedStep(null);

                // Refresh steps (this will filter out the step the user can't see)
                await taskListContext.refreshSteps(updatedStep);

                // Check if user still has View permission for the phase
                // If so, select it; otherwise selections remain cleared
                if (selectedPhase) {
                    const canStillViewPhase = checkPhasePermission(selectedPhase, app, PermissionType.View, false, grant?.id);
                    if (canStillViewPhase) {
                        // Refresh the phase to get updated status
                        const refreshedPhase = await getPhaseById(selectedPhase.id);
                        if (refreshedPhase?.data) {
                            taskListContext.setSelectedPhase(refreshedPhase.data);
                        }
                    }
                }
            }
        },
        [readOnly, step?.id, app, taskListContext, grant]
    );

    const handleActionClick = useCallback(
        async (label: string | null, status: StepStatus) => {
            if (readOnly) return;
            const ok = await confirm(getActionMessage(label, status), "Confirm");
            if (!ok) return;
            if (onConfirm) await onConfirm();
            await onStatusChange(status);
        },
        [readOnly, getActionMessage, onConfirm, onStatusChange]
    );

    const hidePhaseComments = useCallback(() => {
        setVisible(false);
        return true;
    }, []);

    const generateGUID = () =>
        "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
            const r = (Math.random() * 16) | 0,
                v = c === "x" ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });

    const validateTextBox = useCallback(() => {
        const result = newCommentTextValidationGroup.current?.instance.validate();
        const valid = !!result?.isValid;
        newCommentButton.current?.instance.option("disabled", !valid);
        return valid;
    }, []);

    const addComment = useCallback(
        async (_e?: any) => {
            if (readOnly) return;
            const isValid = validateTextBox();
            if (!isValid) return;

            const text = ((newCommentTextbox.current?.instance?.option("value") as string) ?? "").trim();
            const id = generateGUID();
            const newComment = { id, comment: text, audience: selectedAudience.current };

            await addStepRevisionComment(step.id as string, false, newComment);
            await onStatusChange(StepStatus.PENDING_REVISION);

            const updatedStep = taskListContext.getSelectedStep();
            await taskListContext.refreshSteps(updatedStep as StepDto);

            // reset & close
            newCommentTextbox.current?.instance?.option("value", undefined);
            newCommentTextValidationGroup.current?.instance?.reset();
            setVisible(false);
        },
        [readOnly, step?.id, onStatusChange, validateTextBox, taskListContext]
    );

    const handleRevisionClick = useCallback(() => {
        if (readOnly) return;
        if (requireComment) setVisible(true);
        else void handleActionClick("pending revision", StepStatus.PENDING_REVISION);
    }, [readOnly, requireComment, handleActionClick]);

    const cancelRevisionClick = useCallback(() => {
        setVisible(false);
        newCommentTextbox.current?.instance?.option("value", undefined);
        newCommentTextValidationGroup.current?.instance?.reset();
    }, []);

    const handleMarkReadyClick = useCallback(async () => {
        await handleActionClick("ready", StepStatus.READY);
    }, [handleActionClick]);

    const handleMarkCompleteClick = useCallback(async () => {
        // Standard workflow - mark as complete
        await handleActionClick("complete", StepStatus.GRANTEE_COMPLETE);
    }, [handleActionClick]);

    const handleApproveClick = useCallback(async () => {
        await handleActionClick("approve", StepStatus.APPROVED);
    }, [handleActionClick]);

    if (effectiveWorkflowType === PhaseWorkflowType.None) {
        // Upload-only subtasks have no step-level status actions (they are file containers).
        const uploadOnly = isUploadOnlyPhase(cyclePhase);
        const isAlreadyComplete = !!step.completionDate;
        const isAlreadyApproved = step.status === StepStatus.APPROVED;

        // Handler for Mark as Complete in None workflow
        // Just call onComplete - it handles setting completion date, saving, and approving
        const handleNoneWorkflowComplete = async () => {
            if (onComplete) {
                try {
                    await onComplete();
                } catch (e) {
                    // Ignore cancellation errors - these occur when the component unmounts
                    // after the permission check clears the selection (expected behavior)
                    if (e instanceof Error && e.message !== 'canceled') {
                        throw e;
                    }
                }
            }
        };

        // Check if user can transition from current status to APPROVED
        // This respects the transition configuration (not just Edit permission)
        // Pass phase and grant.id for resolving external user roles
        const selectedPhase = taskListContext.getSelectedPhase();
        const canTransitionToApproved = checkStepTransition(step, app, StepStatus.APPROVED, true, selectedPhase, grant?.id);

        return {
            // Workflow type flag
            isNoneWorkflow: true,

            // None workflow state
            isAlreadyComplete,

            // None workflow handlers
            handleSave: onSave || (async () => {}),
            handleComplete: onComplete || (async () => {}),

            // No other state/handlers needed for None workflow
            visible,
            isReadyDisabled,
            isCompleteDisabled,
            popupTitle,
            canMarkReady: false,
            // Upload-only subtasks expose no status buttons at all.
            canMarkComplete: !uploadOnly && !readOnly && !isAlreadyApproved && canTransitionToApproved,
            canRequestRevision: false,
            canApprove: false,
            newCommentTextbox,
            newCommentButton,
            newCommentTextValidationGroup,
            handleActionClick,
            handleRevisionClick,
            cancelRevisionClick,
            hidePhaseComments,
            validateTextBox,
            addComment,

            // Individual button handlers
            handleMarkReadyClick,
            handleMarkCompleteClick: handleNoneWorkflowComplete,
            handleApproveClick,

            // Template mode
            mode,
            handleDeleteSubtask,
            canDeleteSubtask,
        };
    }

    if (effectiveWorkflowType === PhaseWorkflowType.Step) {
        // Determine which buttons to show based on current status and available transitions
        // The availableStepStatuses is computed by backend based on transitions config
        const availableStatuses = step.availableStepStatuses || [];

        // Note: Mark as Ready auto-transitions to GRANTEE_PENDING, so hide button once in that state
        const canMarkReady = availableStatuses.includes(StepStatus.READY);
        // For Step workflow, grantees can mark complete from READY or GRANTEE_PENDING (bypasses Release)
        const canMarkComplete = availableStatuses.includes(StepStatus.GRANTEE_COMPLETE);
        // Request Revision from PENDING goes to PENDING_REVISION
        const canRequestRevision = availableStatuses.includes(StepStatus.PENDING_REVISION);
        const canApprove = availableStatuses.includes(StepStatus.APPROVED);

        // Get selected phase for permission checks after status changes
        const selectedPhase = taskListContext.getSelectedPhase();

        // Helper to handle step status change with permission check
        const handleStepStatusChange = async (updatedStep: StepDto) => {
            const canStillViewStep = checkStepPermission(updatedStep, app, PermissionType.View, true, selectedPhase, grant?.id);

            if (canStillViewStep) {
                // User can still view - update selection and refresh
                taskListContext.setSelectedStep(updatedStep);
                await taskListContext.refreshSteps(updatedStep);
            } else {
                // User will lose view permission - clear selection first, then refresh
                taskListContext.clearSelections();
                taskListContext.setSelectedStep(null);

                await taskListContext.refreshSteps(updatedStep);

                // Check if user still has View permission for the phase
                if (selectedPhase) {
                    const canStillViewPhase = checkPhasePermission(selectedPhase, app, PermissionType.View, false, grant?.id);
                    if (canStillViewPhase) {
                        taskListContext.setSelectedPhase(selectedPhase);
                    }
                }
            }
        };

        // Handlers for Step workflow
        const handleStepReady = async () => {
            if (readOnly || !step.id) return;
            const ok = await confirm("Mark this subtask as ready?", "Confirm");
            if (!ok) return;
            if (onConfirm) await onConfirm();
            const updated = await setStepStatus(step.id, StepStatus.READY);
            if (updated?.data) {
                await handleStepStatusChange(updated.data as StepDto);
            }
        };

        const handleStepComplete = async () => {
            if (readOnly || !step.id) return;
            const ok = await confirm("Mark this subtask as complete?", "Confirm");
            if (!ok) return;
            if (onConfirm) await onConfirm();
            const updated = await setStepStatus(step.id, StepStatus.GRANTEE_COMPLETE);
            if (updated?.data) {
                await handleStepStatusChange(updated.data as StepDto);
            }
        };

        const handleStepApprove = async () => {
            if (readOnly || !step.id) return;
            const ok = await confirm("Approve this subtask?", "Confirm");
            if (!ok) return;
            // Note: onConfirm saves pending changes, but we proceed with status change even if save fails
            // since the approve action is the primary intent
            try {
                if (onConfirm) await onConfirm();
            } catch (e) {
                // Save failed, but continue with status change
                console.warn("Save before approve failed:", e);
            }
            const updated = await setStepStatus(step.id, StepStatus.APPROVED);
            if (updated?.data) {
                await handleStepStatusChange(updated.data as StepDto);
            }
        };

        return {
            // Workflow type flag
            isNoneWorkflow: false,

            // Step workflow handlers
            handleSave: onSave || (async () => {}),
            handleComplete: onComplete || (async () => {}),

            // Button visibility and disabled state
            visible,
            isReadyDisabled,
            isCompleteDisabled,
            popupTitle,
            canMarkReady,
            canMarkComplete,
            canRequestRevision,
            canApprove,
            newCommentTextbox,
            newCommentButton,
            newCommentTextValidationGroup,
            handleActionClick,
            handleRevisionClick,
            cancelRevisionClick,
            hidePhaseComments,
            validateTextBox,
            addComment,

            // Individual button handlers
            handleMarkReadyClick: handleStepReady,
            handleMarkCompleteClick: handleStepComplete,
            handleApproveClick: handleStepApprove,

            // Template mode
            mode,
            handleDeleteSubtask,
            canDeleteSubtask,
        };
    }

    // Standard workflow - use the hooks already defined at the top
    const canMarkReady = available.includes(StepStatus.READY);
    const canMarkComplete = available.includes(StepStatus.GRANTEE_COMPLETE);
    // Request Revision from PENDING goes to PENDING_REVISION (not GRANTEE_PENDING_REVISION)
    const canRequestRevision = available.includes(StepStatus.PENDING_REVISION);
    const canApprove = available.includes(StepStatus.APPROVED);

    return {
        // state
        visible,
        isReadyDisabled,
        isCompleteDisabled,
        popupTitle,

        // derived permissions
        canMarkReady,
        canMarkComplete,
        canRequestRevision,
        canApprove,

        // refs for view controls
        newCommentTextbox,
        newCommentButton,
        newCommentTextValidationGroup,

        // handlers for view
        handleActionClick,
        handleRevisionClick,
        cancelRevisionClick,
        hidePhaseComments,
        validateTextBox,
        addComment,

        // Individual button handlers
        handleMarkReadyClick,
        handleMarkCompleteClick,
        handleApproveClick,

        // None workflow specific (not used in standard workflow)
        isNoneWorkflow: false,
        handleComplete: async () => {},

        // Template mode
        mode,
        handleDeleteSubtask,
        canDeleteSubtask,
    };
}

export type SubTaskStatusButtonsVM = ReturnType<typeof useSubTaskStatusButtonsController>;
